import * as React from 'react'

import { styled } from '@mui/material/styles'
import { Box } from '@mui/material'

import HeaderBar, { HeaderBarHeightPixel } from 'components/atomic/organisms/header'
import ResponsiveContainer from 'components/atomic/organisms/responsive-container'
import SEO, { Breadcrumb } from 'components/atomic/atoms/simple-page-seo'
import ButtonsBottomBar from './components/buttons-bottom-bar'
import SideBar, { SidebarMobile } from './components/side-bar'
import Footer from 'components/atomic/organisms/footer'

interface SideBarLayoutProps {
    children: React.ReactElement | React.ReactElement[]
    showHeader?: boolean
    showFooter?: boolean
    onAbort?: () => void
    onConfirm?: () => void
    onSideBarClose?: () => void
    showButtons?: boolean
    formLoading?: boolean
    contentStyle?: React.CSSProperties
    sideBarRoutes?: Routes | any[]
    sideBarMobileOpen?: boolean
    title?: string
    description?: string
    breadcrumbs?: Breadcrumb[]
}

const Content = styled('div')(({ theme }) => ({
    paddingTop: '2rem',
    paddingBottom: '3rem',
    [theme.breakpoints.up('md')]: {
        paddingLeft: '3rem',
        paddingTop: '3rem',
    },
}))

const Layout = styled('div')(({ marginTop }: { marginTop: number }) => ({
    display: 'flex',
    marginTop: `${marginTop}px`,
}))

const drawerWidth = 240

const SideBarLayout = React.memo(
    ({
        children,
        showHeader = true,
        showFooter = true,
        showButtons = false,
        formLoading = false,
        onAbort,
        onConfirm,
        onSideBarClose,
        contentStyle,
        sideBarRoutes,
        sideBarMobileOpen,
        title,
        description,
        breadcrumbs,
        ...props
    }: SideBarLayoutProps) => {
        const marginTop = React.useMemo(() => (showHeader ? HeaderBarHeightPixel : 0), [showHeader])

        return (
            <>
                {title && <SEO title={title} description={description} breadcrumbs={breadcrumbs} />}

                {showHeader && <HeaderBar />}

                <ResponsiveContainer maxWidth="xl" {...props}>
                    <SidebarMobile appendRoutes={sideBarRoutes} isOpen={sideBarMobileOpen} onClose={onSideBarClose} />

                    <Layout marginTop={marginTop}>
                        <SideBar appendRoutes={sideBarRoutes} />

                        <Box component="main" sx={{ flexGrow: 1, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
                            <Content style={contentStyle}>{children} </Content>
                            {showButtons && (
                                <ButtonsBottomBar
                                    show={showButtons}
                                    loading={formLoading}
                                    handleAbort={onAbort}
                                    handleConfirm={onConfirm}
                                />
                            )}
                        </Box>
                    </Layout>
                </ResponsiveContainer>

                {showFooter && <Footer />}
            </>
        )
    },
)

export default SideBarLayout
